import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from 'components/shared/hooks/useIsomorphicLayoutEffect';

/**
 * Drives a horizontally-scrolling Hub carousel. The scroll container is a
 * native element so we can manipulate it directly via a ref (per repo React
 * conventions) rather than reaching for a heavier carousel library.
 *
 * `canScroll` reports whether the content actually overflows the viewport, so
 * callers can hide the prev/next controls when every card already fits and
 * there is nothing to scroll to. Pass the number of cards so the measurement
 * re-runs when the content width changes.
 */
export function useHubCarousel(itemCount: number) {
  const scrollElRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [canScroll, setCanScroll] = useState(false);

  const measure = useCallback(() => {
    const el = scrollElRef.current;
    // 1px tolerance for sub-pixel rounding, matching the carousel's edge fade.
    setCanScroll(!!el && el.scrollWidth - el.clientWidth > 1);
  }, []);

  // A callback ref measures the moment the scroller mounts (and re-observes if
  // it ever remounts). The ResizeObserver catches viewport-driven width
  // changes; content-count changes are handled by the effect below.
  const scrollRef = useCallback(
    (node: HTMLElement | null) => {
      scrollElRef.current = node;
      resizeObserverRef.current?.disconnect();
      if (node && typeof ResizeObserver !== 'undefined') {
        resizeObserverRef.current = new ResizeObserver(() => measure());
        resizeObserverRef.current.observe(node);
      }
      measure();
    },
    [measure],
  );

  useEffect(() => () => resizeObserverRef.current?.disconnect(), []);

  // Adding or removing cards changes scrollWidth without resizing the scroller's
  // own box, so a ResizeObserver alone would miss it.
  useIsomorphicLayoutEffect(() => {
    measure();
  }, [itemCount, measure]);

  const scrollByDirection = (direction: 1 | -1) => {
    const el = scrollElRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  return {
    scrollRef,
    scrollPrev: () => scrollByDirection(-1),
    scrollNext: () => scrollByDirection(1),
    canScroll,
  };
}
